<template>
    <div>
        <button v-if="!isEdit" type="submit" class="ladda-button btn btn-primary"
                data-app-plugin="ladda" data-style="expand-left"
                @click="loadStudents">
            Fetch Students
        </button>

        <div class="alert alert-warning mt-30 mb-20" role="alert" v-if="data.existing_records.length">
                    <span id="existing-records">
                        <strong>Attention:</strong><br/>
                        There already exist <strong>{{ data.existing_records.length }}</strong> record(s) for this exam —
                        <span v-html="existingRecordLinks()"/>.
                        <br/>
                        <template v-if="data.max_count_per_semester_limit_reached">
                            You cannot add further records for this exam.
                            Please check out the exam(s) above and edit those if necessary.
                        </template>
                        <template v-else>
                            Check out those to make sure you're not entering duplicate record for this exam.
                        </template>
                    </span>
            <button type="button" class="close" data-dismiss="alert" aria-label="Close">
                <span aria-hidden="true">&times;</span>
            </button>
        </div>

        <div class="alert alert-danger mt-10 mb-30" role="alert"
             v-show="hasErrors()">
            <b>Please correct the following errors:</b>
            <ul>
                <li v-for="error in errorsList">{{ error }}</li>
            </ul>
        </div>

      <div v-if="data.card_selection_enabled" class="row ml-0 mt-4">
        <div class="col-md-6 col-lg-4 col-xxl-3">
          <!-- Card -->
          <div class="form-field-container mb--10">
            <FormElement label="Card"
                         label-for-id="card_id"
                         label-class="mb-5 required"
                         :error="errors.card_id">
              <select title="Select Card..."
                      class="form-control"
                      required="required"
                      v-model="data.card_id"
                      v-bind:class="{ 'is-invalid' : errors.card_id }"
                      @change="onCardChange">
                <option disabled value="">Select Card...</option>
                <option v-for="card in data.cards" :value="card.id" :key="card.id">
                  {{ card.short_name ?? card.title }}
                </option>
              </select>
            </FormElement>
          </div>

          <!-- Item -->
          <div v-if="data.item_selection_enabled" class="form-field-container mb--10">
            <FormElement label="Item"
                         label-for-id="item_id"
                         label-class="mb-5 required"
                         :error="errors.item_id">
              <select title="Select Item..."
                      class="form-control selectpicker"
                      required="required"
                      v-model="data.item_id"
                      v-bind:class="{ 'is-invalid' : errors.item_id }">
                <option disabled value="">Select Item...</option>
                <option v-for="(item, index) in collect(data.cards).where('id', data.card_id).first()?.items || []"
                        :value="item.id"
                        :key="item.id">
                  {{ index + 1 }}. {{ item.description ?? item.title }}
                </option>
              </select>
            </FormElement>
          </div>
        </div>
      </div>
      <div v-if="data?.students?.length > 0 && (!data || !data.max_count_per_semester_limit_reached)" class="mt-30">

            <div class="row ml-0">
                <div class="col-md-6 col-xxl-4">
                    <!-- Entry Date -->
                    <div class="form-field-container mb--10">
                        <FormElement label="Exam Date"
                                     label-for-id="exam_date"
                                     label-class="mb-5"
                                     :error="errors.exam_date">
                            <DatePicker reference="exam_date"
                                        :extras="{ endDate: '0d' }"
                                        :on-change="onDateChanged"
                                        :error="errors.exam_date"
                                        :value="data.exam_date ? moment(data.exam_date).toDate() : null"/>
                        </FormElement>
                    </div>

                    <!-- Publish Date -->
                    <div class="form-field-container mb--10">
                        <FormElement label="Publish Time"
                                     label-for-id="publish_time"
                                     label-class="required"
                                     :error="errors.publish_date">
                            <div class="d-flex" style="gap: 10px">
                                <DatePicker :required="true"
                                            reference="publish_date"
                                            :on-change="onDateChanged"
                                            :error="errors.publish_date"
                                            :value="data.publish_date ? data.publish_date : null"/>
                                <TimePicker :required="true"
                                            reference="publish_time"
                                            :on-change="onTimeChanged"
                                            :error="errors.publish_time"
                                            :value="data.publish_time ? data.publish_time : new Date()"/>
                            </div>
                        </FormElement>
                    </div>

                    <!-- Exam Name -->
                    <div class="form-field-container mb--10">
                        <FormElement :error="errors.exam_name"
                                     label="Exam Name"
                                     label-for-id="exam_name">
                            <!-- The 'list', 'autocomplete' and 'required' attributes are specific to EIS only -->
                            <input type="text" class="vertical form-control"
                                   :class="{ 'is-invalid': errors?.exam_name !== null }"
                                   :list="data.exam === 513 ? 'eis-ct-exams-half-yearly' : (data.exam === 514 ? 'eis-ct-exams-year-final' : null)"
                                   :autocomplete="[513, 514].includes(data.exam) ? 'off' : null"
                                   :required="[513, 514].includes(data.exam) ? 'required' : null"
                                   v-model="data.exam_name"/>

                            <datalist id="eis-ct-exams-half-yearly">
                                <option value="1st Tutorial"/>
                                <option value="2nd Tutorial"/>
                            </datalist>
                            <datalist id="eis-ct-exams-year-final">
                                <option value="3rd Tutorial"/>
                                <option value="4th Tutorial"/>
                            </datalist>
                        </FormElement>
                    </div>

                    <div v-if="data.competency_based_curriculum && !isEdit">

                        <!-- Assessment Type -->
                        <div class="form-field-container mb--10">
                            <FormElement :error="errors.exam_type"
                                         label="Assessment Type">
                                <radio-toggles
                                    v-model="competencyBasedCurriculum.examType"
                                    :options="[
                                    {value: 'pi', label: 'PI'},
                                    {value: 'bi', label: 'BI'}
                                ]"
                                    @change="competencyBasedCurriculum.noOfColumns = 1"
                                />
                            </FormElement>
                        </div>

                        <!-- No of columns -->
                        <div class="form-field-container mb--10">
                            <FormElement label="No of columns"
                                         label-for-id="no_of_columns">
                                <select class="form-control" id="no_of_columns"
                                        v-model="competencyBasedCurriculum.noOfColumns">
                                    <option
                                        v-for="i in data[`${competencyBasedCurriculum.examType}s`].length"
                                        :key="i"
                                        :value="i"
                                    >
                                        {{ i }}
                                    </option>
                                </select>
                            </FormElement>
                        </div>
                    </div>
                </div>
            </div>

            <table
                v-if="!data.competency_based_curriculum"
                id="marks-entry-form"
                class="table data-table dataTable table-bordered table-striped table-responsive border-0"
            >
                <tbody>
                <tr v-if="isMultiExamMode()">
                    <td rowspan="4" v-if="!showRoll">SL</td>
                    <td class="hidden-sm-down" rowspan="4" v-if="data.has_groups">Group</td>
                    <td class="hidden-sm-down" rowspan="4">ID</td>
                    <td class="hidden-sm-down" rowspan="4" v-if="showPhoto">Photo</td>
                    <td class="hidden-sm-down" rowspan="4" v-if="showRoll">Roll</td>
                    <td rowspan="2">Name</td>
                    <td v-for="examId of orderedExamIds()" :key="examId"
                        :colspan="(data.subject_object.has_subjective ? 1 : 0)
                                   + (data.subject_object.has_objective ? 1 : 0)
                                   + (data.subject_object.has_practical ? 1 : 0)
                                   + (showTotalColumn() ? 1 : 0)
                                   + (canDeleteMarksEntry ? 1 : 0)
                                   + 1"
                    >
                        {{ data.exam[examId] }}
                    </td>
                </tr>
                <tr v-if="isMultiExamMode()">
                    <template v-for="examId of orderedExamIds()" :key="examId">
                        <td class="w-80" v-if="data.subject_object.has_subjective">
                            {{ data.subject_object.has_objective || data.subject_object.has_practical ? 'Subjective' : 'Marks' }}
                        </td>
                        <td class="w-80" v-if="data.subject_object.has_objective">Objective</td>
                        <td class="w-80" v-if="data.subject_object.has_practical">Practical</td>
                        <td class="w-80" v-if="showTotalColumn()">Total</td>
                        <td class="w-80" v-if="showGradeColumn()" rowspan="3" style="border-left-width: 1px">Grade</td>
                        <td rowspan="3">Absent</td>
                        <td rowspan="3" v-if="showNotEligibleColumn">Not<br/>Eligible</td>
                        <td rowspan="3" v-if="showRemarksColumn">Remarks</td>
                        <td rowspan="3" v-if="canDeleteMarksEntry">Action</td>
                    </template>
                </tr>
                <tr v-if="!isMultiExamMode()">
                    <td rowspan="3" v-if="!showRoll">SL</td>
                    <td class="hidden-sm-down" rowspan="3" v-if="data.has_groups">Group</td>
                    <td class="hidden-sm-down" rowspan="3">ID</td>
                    <td class="hidden-sm-down" rowspan="3" v-if="showPhoto">Photo</td>
                    <td class="hidden-sm-down" rowspan="3" v-if="showRoll">Roll</td>
                    <td>Name</td>
                    <td class="w-80" v-if="data.subject_object.has_subjective">
                        {{ data.exam_has_subj_obj_prac_breakdown && (data.subject_object.has_objective || data.subject_object.has_practical) ? 'Subjective' : 'Marks' }}
                    </td>
                    <td class="w-80" v-if="data.exam_has_subj_obj_prac_breakdown && data.subject_object.has_objective">Objective</td>
                    <td class="w-80" v-if="data.exam_has_subj_obj_prac_breakdown && data.subject_object.has_practical">Practical</td>
                    <td class="w-80" v-if="data.exam_has_subj_obj_prac_breakdown && showTotalColumn()">Total</td>
                    <td class="w-80" v-if="showGradeColumn()" rowspan="3" style="border-left-width: 1px">Grade</td>
                    <td rowspan="3">Absent</td>
                    <td rowspan="3" v-if="showNotEligibleColumn">Not<br/>Eligible</td>
                    <td rowspan="3" v-if="showRemarksColumn">Remarks</td>
                    <td rowspan="3" v-if="canDeleteMarksEntry">Action</td>
                </tr>
                <tr>
                    <td>Full Marks <span class="text-danger">*</span></td>
                    <template v-if="isMultiExamMode()">
                        <template v-for="examId of orderedExamIds()" :key="examId">
                            <td v-if="data.subject_object.has_subjective">
                                <input type="number" min="0" class="vertical form-control w-80 text-right"
                                       v-bind:class="{ 'is-invalid': multiExams[examId].errors?.fullMarks?.subjective !== null }"
                                       v-model="multiExams[examId].fullMarks.subjective"
                                       @change="calculateFullMarksTotal(examId)"/>
                                <small class="invalid-feedback d-block" v-show="multiExams[examId].errors?.fullMarks?.subjective !== null">
                                    {{ multiExams[examId].errors?.fullMarks?.subjective || '' }}
                                </small>
                            </td>
                            <td v-if="data.subject_object.has_objective">
                                <input type="number" min="0" class="vertical form-control w-80 text-right"
                                       v-bind:class="{ 'is-invalid': multiExams[examId].errors?.fullMarks?.objective !== null }"
                                       v-model="multiExams[examId].fullMarks.objective"
                                       @change="calculateFullMarksTotal(examId)"/>
                                <small class="invalid-feedback d-block" v-show="multiExams[examId].errors?.fullMarks?.objective !== null">
                                    {{ multiExams[examId].errors?.fullMarks?.objective || '' }}
                                </small>
                            </td>
                            <td v-if="data.subject_object.has_practical">
                                <input type="number" min="0" class="vertical form-control w-80 text-right"
                                       v-bind:class="{ 'is-invalid': multiExams[examId].errors?.fullMarks?.practical !== null }"
                                       v-model="multiExams[examId].fullMarks.practical"
                                       @change="calculateFullMarksTotal(examId)"/>
                                <small class="invalid-feedback d-block" v-show="multiExams[examId].errors?.fullMarks?.practical !== null">
                                    {{ multiExams[examId].errors?.fullMarks?.practical || '' }}
                                </small>
                            </td>
                            <td class="w-80 text-right" v-if="showTotalColumn()">{{ multiExams[examId].fullMarks.total }}</td>
                        </template>
                    </template>
                    <template v-else>
                        <td v-if="data.subject_object.has_subjective">
                            <input type="number" min="0" class="vertical form-control w-80 text-right"
                                   v-bind:class="{ 'is-invalid': errors?.fullMarks?.subjective !== null }"
                                   v-model="fullMarks.subjective"
                                   @change="calculateFullMarksTotal()"/>
                            <small class="invalid-feedback d-block" v-show="errors?.fullMarks?.subjective !== null">
                                {{ errors?.fullMarks?.subjective || '' }}
                            </small>
                        </td>
                        <td v-if="data.exam_has_subj_obj_prac_breakdown && data.subject_object.has_objective">
                            <input type="number" min="0" class="vertical form-control w-80 text-right"
                                   v-bind:class="{ 'is-invalid': errors?.fullMarks?.objective !== null }"
                                   v-model="fullMarks.objective"
                                   @change="calculateFullMarksTotal()"/>
                            <small class="invalid-feedback d-block" v-show="errors?.fullMarks?.objective !== null">
                                {{ errors?.fullMarks?.objective || '' }}
                            </small>
                        </td>
                        <td v-if="data.exam_has_subj_obj_prac_breakdown && data.subject_object.has_practical">
                            <input type="number" min="0" class="vertical form-control w-80 text-right"
                                   v-bind:class="{ 'is-invalid': errors?.fullMarks?.practical !== null }"
                                   v-model="fullMarks.practical"
                                   @change="calculateFullMarksTotal()"/>
                            <small class="invalid-feedback d-block" v-show="errors?.fullMarks?.practical !== null">
                                {{ errors?.fullMarks?.practical || '' }}
                            </small>
                        </td>
                        <td class="w-80 text-right" v-if="data.exam_has_subj_obj_prac_breakdown && showTotalColumn()">{{ fullMarks.total }}</td>
                    </template>
                </tr>
                <tr>
                    <td class="highlight">Pass Marks</td>
                    <template v-if="isMultiExamMode()">
                        <template v-for="examId of orderedExamIds()" :key="examId">
                            <td v-if="data.subject_object.has_subjective" class="highlight">
                                <input type="number" min="0" class="vertical form-control w-80 text-right"
                                       v-bind:class="{ 'is-invalid': multiExams[examId].errors?.passMarks?.subjective !== null }"
                                       v-model="multiExams[examId].passMarks.subjective"
                                       @change="calculatePassMarksTotal(examId)"/>
                                <small class="invalid-feedback d-block" v-show="multiExams[examId].errors?.passMarks?.subjective !== null">
                                    {{ multiExams[examId].errors?.passMarks?.subjective || '' }}
                                </small>
                            </td>
                            <td v-if="data.subject_object.has_objective" class="highlight">
                                <input type="number" min="0" class="vertical form-control w-80 text-right"
                                       v-bind:class="{ 'is-invalid': multiExams[examId].errors?.passMarks?.objective !== null }"
                                       v-model="multiExams[examId].passMarks.objective"
                                       @change="calculatePassMarksTotal(examId)"/>
                                <small class="invalid-feedback d-block" v-show="multiExams[examId].errors?.passMarks?.objective !== null">
                                    {{ multiExams[examId].errors?.passMarks?.objective || '' }}
                                </small>
                            </td>
                            <td v-if="data.subject_object.has_practical" class="highlight">
                                <input type="number" min="0" class="vertical form-control w-80 text-right"
                                       v-bind:class="{ 'is-invalid': multiExams[examId].errors?.passMarks?.practical !== null }"
                                       v-model="multiExams[examId].passMarks.practical"
                                       @change="calculatePassMarksTotal(examId)"/>
                                <small class="invalid-feedback d-block" v-show="multiExams[examId].errors?.passMarks?.practical !== null">
                                    {{ multiExams[examId].errors?.passMarks?.practical || '' }}
                                </small>
                            </td>
                            <td class="w-80 text-right highlight" v-if="showTotalColumn()">
                                <input type="number" min="0" class="vertical form-control w-80 text-right"
                                       v-bind:class="{ 'is-invalid': multiExams[examId].errors?.passMarks?.total !== null }"
                                       v-model="multiExams[examId].passMarks.total"/>
                                <small class="invalid-feedback d-block" v-show="multiExams[examId].errors?.passMarks?.total !== null">
                                    {{ multiExams[examId].errors?.passMarks?.total || '' }}
                                </small>
                            </td>
                        </template>
                    </template>
                    <template v-else>
                        <td v-if="data.subject_object.has_subjective">
                            <input type="number" min="0" class="vertical form-control w-80 text-right"
                                   v-bind:class="{ 'is-invalid': errors?.passMarks?.subjective !== null }"
                                   v-model="passMarks.subjective"
                                   @change="calculatePassMarksTotal()"/>
                            <small class="invalid-feedback d-block" v-show="errors?.passMarks?.subjective !== null">
                                {{ errors?.passMarks?.subjective || '' }}
                            </small>
                        </td>
                        <td v-if="data.exam_has_subj_obj_prac_breakdown && data.subject_object.has_objective">
                            <input type="number" min="0" class="vertical form-control w-80 text-right"
                                   v-bind:class="{ 'is-invalid': errors?.passMarks?.objective !== null }"
                                   v-model="passMarks.objective"
                                   @change="calculatePassMarksTotal()"/>
                            <small class="invalid-feedback d-block" v-show="errors?.passMarks?.objective !== null">
                                {{ errors?.passMarks?.objective || '' }}
                            </small>
                        </td>
                        <td v-if="data.exam_has_subj_obj_prac_breakdown && data.subject_object.has_practical">
                            <input type="number" min="0" class="vertical form-control w-80 text-right"
                                   v-bind:class="{ 'is-invalid': errors?.passMarks?.practical !== null }"
                                   v-model="passMarks.practical"
                                   @change="calculatePassMarksTotal()"/>
                            <small class="invalid-feedback d-block" v-show="errors?.passMarks?.practical !== null">
                                {{ errors?.passMarks?.practical || '' }}
                            </small>
                        </td>
                        <td class="w-80 text-right" v-if="data.exam_has_subj_obj_prac_breakdown && showTotalColumn()">
                            <input type="number" min="0" class="vertical form-control w-80 text-right"
                                   v-bind:class="{ 'is-invalid': errors?.passMarks?.total !== null }"
                                   v-model="passMarks.total"/>
                            <small class="invalid-feedback d-block" v-show="errors?.passMarks?.total !== null">
                                {{ errors?.passMarks?.total || '' }}
                            </small>
                        </td>
                    </template>
                </tr>
                <tr v-for="(student, index) in data.students" :key="index">
                    <td class="text-center" v-if="!showRoll">{{ index + 1 }}</td>
                    <td class="hidden-sm-down text-capitalize text-center" v-if="data.has_groups">{{ student.group_name }}</td>
                    <td class="hidden-sm-down">{{ student.display_id }}</td>
                    <td class="hidden-sm-down text-center" v-if="showPhoto">
                        <span class="avatar">
                            <img :src="student.photo" alt="" style="width: 40px; height: 40px; border: 1px solid #eeeeee"/>
                        </span>
                    </td>
                    <td class="hidden-sm-down text-center" v-if="showRoll">{{ student.roll }}</td>
                    <td>
                        <div class="text-primary">{{ student.name }}</div>
                        <small class="hidden-md-up text-secondary">
                            <span class="d-block text-capitalize" v-if="data.has_groups">
                                {{ student.group_name }}
                            </span>
                            ID: {{ student.display_id }}
                            <span class="d-block" v-if="student.roll">
                                Roll: {{ student.roll }}
                            </span>
                        </small>
                    </td>
                    <template v-if="isMultiExamMode()">
                        <template v-for="examId of orderedExamIds()" :key="examId">
                            <td v-if="data.subject_object.has_subjective">
                              <div class="d-flex align-items-center">
                                <div class="abc-checkbox absent abc-checkbox-circle abc-checkbox-danger"
                                     :class="{'mb-20' : !multiExams[examId].data.students[index].is_absent
                                            && !multiExams[examId].data.students[index].is_absent_subjective}"
                                     v-if="subjectiveObjectivePracticalAbsentMarkingEnabled && !multiExams[examId].data.students[index].is_absent">
                                  <input :id="`absent-subjective-${index}-${examId}`" type="checkbox" title="Absent"
                                         v-model="multiExams[examId].data.students[index].is_absent_subjective"
                                         @change="onAbsentSubjectiveChanged(index, examId)" tabindex="-1"/>
                                  <label :for="`absent-subjective-${index}-${examId}`"></label>
                                </div>
                                <div class="flex-grow-1 ml-2"
                                     v-if="!multiExams[examId].data.students[index].is_absent && !multiExams[examId].data.students[index].is_absent_subjective">
                                  <input type="number" min="0" class="vertical form-control w-80 text-right"
                                         v-if="!multiExams[examId].data.students[index].is_absent"
                                         :disabled="multiExams[examId].data.students[index].is_failed_item
                                                  || multiExams[examId].data.students[index].is_failed_card"
                                         v-bind:class="{ 'is-invalid': multiExams[examId].errors?.subjective[index] || false }"
                                         v-model.number="multiExams[examId].data.students[index].subjective"
                                         @change="calculateObtainedTotal(index, examId)"
                                         @focus="e => e.target.select()"/>
                                  <small class="invalid-feedback d-block" v-show="multiExams[examId].errors?.subjective[index] || false">
                                      {{ multiExams[examId].errors?.subjective[index] || '' }}
                                  </small>
                                </div>
                              </div>
                            </td>
                            <td v-if="data.subject_object.has_objective">
                              <div class="d-flex align-items-center">
                                <div class="abc-checkbox absent abc-checkbox-circle abc-checkbox-danger"
                                     :class="{'mb-20' : !multiExams[examId].data.students[index].is_absent
                                            && !multiExams[examId].data.students[index].is_absent_objective}"
                                     v-if="subjectiveObjectivePracticalAbsentMarkingEnabled && !multiExams[examId].data.students[index].is_absent">
                                  <input :id="`absent-objective-${index}-${examId}`" type="checkbox" title="Absent"
                                         v-model="multiExams[examId].data.students[index].is_absent_objective"
                                         @change="onAbsentObjectiveChanged(index, examId)" tabindex="-1"/>
                                  <label :for="`absent-objective-${index}-${examId}`"></label>
                                </div>
                                <div class="flex-grow-1 ml-2"
                                     v-if="!multiExams[examId].data.students[index].is_absent && !multiExams[examId].data.students[index].is_absent_objective">
                                <input type="number" min="0" class="vertical form-control w-80 text-right"
                                       v-if="!multiExams[examId].data.students[index].is_absent"
                                       v-bind:class="{ 'is-invalid': multiExams[examId].errors?.objective[index] || false }"
                                       v-model.number="multiExams[examId].data.students[index].objective"
                                       @change="calculateObtainedTotal(index, examId)"
                                       @focus="e => e.target.select()"/>
                                <small class="invalid-feedback d-block" v-show="multiExams[examId].errors?.objective[index] || false">
                                    {{ multiExams[examId].errors?.objective[index] || '' }}
                                </small>
                                </div>
                              </div>
                            </td>
                            <td v-if="data.subject_object.has_practical">
                              <div class="d-flex align-items-center">
                                <div class="abc-checkbox absent abc-checkbox-circle abc-checkbox-danger"
                                     :class="{'mb-20' : !multiExams[examId].data.students[index].is_absent
                                            && !multiExams[examId].data.students[index].is_absent_practical}"
                                     v-if="subjectiveObjectivePracticalAbsentMarkingEnabled && !multiExams[examId].data.students[index].is_absent">
                                  <input :id="`absent-practical-${index}-${examId}`" type="checkbox" title="Absent"
                                         v-model="multiExams[examId].data.students[index].is_absent_practical"
                                         @change="onAbsentPracticalChanged(index, examId)" tabindex="-1"/>
                                  <label :for="`absent-practical-${index}-${examId}`"></label>
                                </div>
                                <div class="flex-grow-1 ml-2"
                                     v-if="!multiExams[examId].data.students[index].is_absent && !multiExams[examId].data.students[index].is_absent_practical">
                                <input type="number" min="0" class="vertical form-control w-80 text-right"
                                       v-if="!multiExams[examId].data.students[index].is_absent"
                                       v-bind:class="{ 'is-invalid': multiExams[examId].errors?.practical[index] || false }"
                                       v-model.number="multiExams[examId].data.students[index].practical"
                                       @change="calculateObtainedTotal(index, examId)"
                                       @focus="e => e.target.select()"/>
                                <small class="invalid-feedback d-block" v-show="multiExams[examId].errors?.practical[index] || false">
                                    {{ multiExams[examId].errors?.practical[index] || '' }}
                                </small>
                                </div>
                              </div>
                            </td>
                            <td class="w-80 text-right" v-if="showTotalColumn()">{{ multiExams[examId].data.students[index].total }}</td>
                            <td class="w-80 text-center" v-if="showGradeColumn()">{{ multiExams[examId].data.students[index].grade ?? '' }}</td>
                            <td>
                                <div class="abc-checkbox absent abc-checkbox-circle abc-checkbox-danger">
                                    <input :id="`absent-${index}-${examId}`" type="checkbox" title="Absent"
                                           v-model="multiExams[examId].data.students[index].is_absent"
                                           @change="onAbsentChanged(index, examId)" tabindex="-1"/>
                                    <label :for="`absent-${index}-${examId}`"></label>
                                </div>
                            </td>
                            <td v-if="showNotEligibleColumn">
                                <div class="abc-checkbox absent abc-checkbox-circle abc-checkbox-danger"
                                     v-if="!multiExams[examId].data.students[index].is_absent">
                                    <input :id="`not-eligible-${index}-${examId}`" type="checkbox" title="Not Eligible"
                                           v-model="multiExams[examId].data.students[index].is_not_eligible"
                                           tabindex="-1"/>
                                    <label :for="`not-eligible-${index}-${examId}`"></label>
                                </div>
                            </td>
                            <td v-if="showRemarksColumn">
                                <input type="text" class="vertical form-control w-120 text-left"
                                       v-if="!multiExams[examId].data.students[index].is_absent"
                                       v-bind:class="{ 'is-invalid': multiExams[examId].errors?.remarks[index] || false }"
                                       v-model="multiExams[examId].data.students[index].remarks"
                                       @focus="e => e.target.select()"/>
                                <small class="invalid-feedback d-block" v-show="multiExams[examId].errors?.remarks[index] || false">
                                    {{ multiExams[examId].errors?.remarks[index] || '' }}
                                </small>
                            </td>
                            <td v-if="canDeleteMarksEntry">

                            </td>
                        </template>
                    </template>
                    <template v-else>
                        <td v-if="data.subject_object.has_subjective">
                          <div class="d-flex align-items-center">
                            <div class="abc-checkbox absent abc-checkbox-circle abc-checkbox-danger"
                                 :class="{'mb-20' : !data.students[index].is_absent && !data.students[index].is_absent_subjective}"
                                 v-if="subjectiveObjectivePracticalAbsentMarkingEnabled && !data.students[index].is_absent">
                              <input :id="`absent-subjective-${index}`" type="checkbox" title="Absent"
                                     v-model="data.students[index].is_absent_subjective"
                                     @change="onAbsentSubjectiveChanged(index)" tabindex="-1"/>
                              <label :for="`absent-subjective-${index}`"></label>
                            </div>
                            <div class="flex-grow-1 ml-2"
                                 v-if="!data.students[index].is_absent && !data.students[index].is_absent_subjective">
                              <input type="number" min="0" class="vertical form-control w-80 text-right"
                                     v-bind:class="{ 'is-invalid': errors?.subjective[index] || false }"
                                     :disabled="data.students[index].is_failed_item
                                                || data.students[index].is_failed_card"
                                     v-model="data.students[index].subjective"
                                     @change="calculateObtainedTotal(index)"
                                     @focus="e => e.target.select()"/>
                              <small class="invalid-feedback d-block" v-show="errors?.subjective[index] || false">
                                {{ errors?.subjective[index] || '' }}
                              </small>
                            </div>
                          </div>
                        </td>
                        <td v-if="data.exam_has_subj_obj_prac_breakdown && data.subject_object.has_objective">
                          <div class="d-flex align-items-center">
                            <div class="abc-checkbox absent abc-checkbox-circle abc-checkbox-danger"
                                 :class="{'mb-20' : !data.students[index].is_absent && !data.students[index].is_absent_objective}"
                                 v-if="subjectiveObjectivePracticalAbsentMarkingEnabled && !data.students[index].is_absent">
                              <input :id="`absent-objective-${index}`" type="checkbox" title="Absent"
                                     v-model="data.students[index].is_absent_objective"
                                     @change="onAbsentObjectiveChanged(index)" tabindex="-1"/>
                              <label :for="`absent-objective-${index}`"></label>
                            </div>
                            <div class="flex-grow-1 ml-2"
                                 v-if="!data.students[index].is_absent && !data.students[index].is_absent_objective">
                              <input type="number" min="0" class="vertical form-control w-80 text-right"
                                     v-bind:class="{ 'is-invalid': errors?.objective[index] || false }"
                                     v-model="data.students[index].objective"
                                     @change="calculateObtainedTotal(index)"
                                     @focus="e => e.target.select()"/>
                              <small class="invalid-feedback d-block" v-show="errors?.objective[index] || false">
                                  {{ errors?.objective[index] || '' }}
                              </small>
                            </div>
                          </div>
                        </td>
                        <td v-if="data.exam_has_subj_obj_prac_breakdown && data.subject_object.has_practical">
                          <div class="d-flex align-items-center">
                            <div class="abc-checkbox absent abc-checkbox-circle abc-checkbox-danger"
                                 :class="{'mb-20' : !data.students[index].is_absent && !data.students[index].is_absent_practical}"
                                 v-if="subjectiveObjectivePracticalAbsentMarkingEnabled && !data.students[index].is_absent">
                              <input :id="`absent-practical-${index}`" type="checkbox" title="Absent"
                                     v-model="data.students[index].is_absent_practical"
                                     @change="onAbsentPracticalChanged(index)" tabindex="-1"/>
                              <label :for="`absent-practical-${index}`"></label>
                            </div>
                            <div class="flex-grow-1 ml-2"
                                 v-if="!data.students[index].is_absent && !data.students[index].is_absent_practical">
                              <input type="number" min="0" class="vertical form-control w-80 text-right"
                                     v-bind:class="{ 'is-invalid': errors?.practical[index] || false }"
                                     v-model="data.students[index].practical"
                                     @change="calculateObtainedTotal(index)"
                                     @focus="e => e.target.select()"/>
                              <small class="invalid-feedback d-block" v-show="errors?.practical[index] || false">
                                  {{ errors?.practical[index] || '' }}
                              </small>
                            </div>
                          </div>
                        </td>
                        <td class="w-80 text-right" v-if="data.exam_has_subj_obj_prac_breakdown && showTotalColumn()">{{ data.students[index].total }}</td>
                        <td class="w-80 text-center" v-if="showGradeColumn()">{{ data.students[index].grade ?? '' }}</td>
                        <td>
                            <div class="abc-checkbox absent abc-checkbox-circle abc-checkbox-danger">
                                <input :id="`absent-${index}`" type="checkbox" title="Absent"
                                       v-model="data.students[index].is_absent"
                                       @change="onAbsentChanged(index)" tabindex="-1"/>
                                <label :for="`absent-${index}`"></label>
                            </div>
                        </td>
                        <td v-if="showNotEligibleColumn">
                            <div class="abc-checkbox absent abc-checkbox-circle abc-checkbox-danger"
                                 v-if="!data.students[index].is_absent">
                                <input :id="`not-eligible-${index}`" type="checkbox" title="Not Eligible"
                                       v-model="data.students[index].is_not_eligible"
                                       tabindex="-1"/>
                                <label :for="`not-eligible-${index}`"></label>
                            </div>
                        </td>
                        <td v-if="showRemarksColumn">
                            <input type="text" class="vertical form-control w-120 text-left"
                                   v-if="!data.students[index].is_absent"
                                   v-bind:class="{ 'is-invalid': errors?.remarks[index] || false }"
                                   v-model="data.students[index].remarks"
                                   @focus="e => e.target.select()"/>
                            <small class="invalid-feedback d-block" v-show="errors?.remarks[index] || false">
                                {{ errors?.remarks[index] || '' }}
                            </small>
                        </td>
                        <td class="text-center" v-if="canDeleteMarksEntry">
                            <ActionButtons :entry="data.students[index]"
                                           :index="index"
                                           :remove-entry="this.onRemoveEntry"
                                           :has-delete-permission="canDeleteMarksEntry"/>
                        </td>
                    </template>
                </tr>
                </tbody>
            </table>

            <table
                v-else
                id="competency-based-assessment-form"
                class="table data-table dataTable table-bordered table-striped border-0 w-auto"
            >
                <thead>
                <tr>
                    <th class="hidden-sm-down" rowspan="2" v-if="data.has_groups">Group</th>
                    <th class="hidden-sm-down" rowspan="2">ID</th>
                    <th class="hidden-sm-down" rowspan="2" v-if="showPhoto">Photo</th>
                    <th class="hidden-sm-down" rowspan="2" v-if="showRoll">Roll</th>
                    <th rowspan="2">Name</th>
                    <th :colspan="competencyBasedCurriculum.noOfColumns" class="text-center">
                        {{ competencyBasedCurriculum.examType === 'pi' ? 'Performance Indicators' : 'Behavioral Indicators' }}
                        <a class="btn text-white d-block d-md-inline ml-5" href="javascript:void(0)"
                           @click="window.showIndicatorDetailsModal(data[`${competencyBasedCurriculum.examType}s`], competencyBasedCurriculum.examType)">
                            <i class="fa-solid fa-info-circle mr-3"></i>
                            View Details
                        </a>
                    </th>
                    <th rowspan="2" v-if="canDeleteMarksEntry">Action</th>
                </tr>
                <tr>
                    <th v-for="col in competencyBasedCurriculum.noOfColumns" :key="col">
                        <select
                            v-model="competencyBasedCurriculum.exams[competencyBasedCurriculum.examType][col - 1]"
                            class="form-control"
                            v-bind:class="{ 'is-invalid': (competencyBasedCurriculum.exams.errors.exams[col - 1] || null) !== null }"
                        >
                            <option value="" disabled>Select...</option>
                            <option
                                v-for="(indicator, index) in data[`${competencyBasedCurriculum.examType}s`]"
                                :key="index"
                                :value="indicator.id"
                            >
                                {{ indicator[`${competencyBasedCurriculum.examType}_no`] }}
                            </option>
                        </select>
                        <small class="invalid-feedback d-block" v-show="(competencyBasedCurriculum.exams.errors.exams[col - 1] || null) !== null">
                            {{ competencyBasedCurriculum.exams.errors.exams[col - 1] || '' }}
                        </small>
                    </th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="(student, index) in data.students" :key="index">
                    <td class="hidden-sm-down text-capitalize text-center" v-if="data.has_groups">{{ student.group_name }}</td>
                    <td class="hidden-sm-down">{{ student.display_id }}</td>
                    <td class="hidden-sm-down text-center" v-if="showPhoto">
                        <span class="avatar">
                            <img :src="student.photo" alt="" style="width: 40px; height: 40px; border: 1px solid #eeeeee"/>
                        </span>
                    </td>
                    <td class="hidden-sm-down text-center" v-if="showRoll">{{ student.roll }}</td>
                    <th>
                        <div class="name text-primary">{{ student.name }}</div>
                        <small class="hidden-md-up text-secondary">
                            <span class="d-block text-capitalize" v-if="data.has_groups">
                                {{ student.group_name }}
                            </span>
                            ID: {{ student.display_id }}
                            <span class="d-block" v-if="student.roll">
                                Roll: {{ student.roll }}
                            </span>
                        </small>
                    </th>
                    <td v-for="col in competencyBasedCurriculum.noOfColumns" :key="col" class="input">
                        <radio-toggles
                            v-if="competencyBasedCurriculum.exams[competencyBasedCurriculum.examType][col - 1] > 0"
                            v-model="data.students[index][competencyBasedCurriculum.examType][col - 1]"
                            :options="piBiLevels"
                            :same-selection-unsets-value="true"
                            :toggle-orientation-on-small-screen="true"
                            :has-error="competencyBasedCurriculum.exams.errors.levels?.[col - 1]?.[index] !== null"
                            :error-msg="competencyBasedCurriculum.exams.errors.levels?.[col - 1]?.[index] || ''"
                        />
                    </td>
                    <td class="text-center" v-if="canDeleteMarksEntry">
                      <ActionButtons :entry="data.students[index]"
                                     :index="index"
                                     :remove-entry="this.onRemoveEntry"
                                     :has-delete-permission="canDeleteMarksEntry"/>
                    </td>
                </tr>
                </tbody>
            </table>

            <div v-if="this.sendSms && !this.isEdit" class="mt-20 mb-30">
              <div class="form-group">
                <div class="abc-checkbox abc-checkbox-circle abc-checkbox-success">
                  <input name="send_sms" id="send-sms" type="checkbox" v-model="data.send_sms"/>
                  <label for="send-sms" class="pl-20" style="padding-top: 8px">
                    Send SMS
                  </label>
                </div>
              </div>
            </div>

            <button type="submit" class="ladda-button btn btn-primary btn-outline mt-20"
                    data-app-plugin="ladda" data-style="expand-left"
                    @click="submitMarks(true)">
                Save & Continue Editing
            </button>

            <button type="submit" class="ladda-button btn btn-primary mt-20 ml-20"
                    data-app-plugin="ladda" data-style="expand-left"
                    @click="submitMarks(false)">
                Submit
            </button>

        </div>
    </div>
</template>

<script>
import {ref} from 'vue';
import FormElement from '../../../components/FormElement';
import DatePicker from "../../../components/DatePicker";
import TimePicker from "../../../components/TimePicker";
import ActionButtons from "../../../accounts/billing/components/ActionButtons.vue";
import RadioToggles from "../../../components/RadioToggles.vue";
import moment from "moment";
import {collect} from "collect.js";

export default {
    components: {
        RadioToggles,
        ActionButtons,
        FormElement,
        DatePicker,
        TimePicker,
    },
    props: {
        isMedicalCollege: Boolean,
        isEdit: {
            type: Boolean,
            required: true,
        },
        isDuplicating: {
            type: Boolean,
            required: true,
        },
        canDeleteMarksEntry: {
            type: Boolean,
            required: true,
        },
        examRecord: {
            type: Object,
            required: true,
        },
        examRecords: {
          type: Array,
          required: false,
        },
        pis: {
            type: Array,
            required: false,
            default: [],
        },
        bis: {
            type: Array,
            required: false,
            default: [],
        },
        piBiLevelDetails: {
            type: Object,
            required: false,
            default: {},
        },
        showNotEligibleColumn: {
            type: Boolean,
            required: false,
            default: false,
        },
        showRemarksColumn: {
            type: Boolean,
            required: false,
            default: false,
        },
        subjectiveObjectivePracticalAbsentMarkingEnabled: {
          type: Boolean,
          required: false,
          default: false,
        },
        showPhoto: {
            type: Boolean,
            required: false,
            default: false,
        },
        showRoll: {
            type: Boolean,
            required: false,
            default: true,
        },
        sendSms: {
              type: Boolean,
              default: false,
        }
    },
    setup() {
        return {
            competencyBasedCurriculum: ref({
                examType: ref('pi'),
                noOfColumns: ref(1),
                exams: ref({
                    pi: [],
                    bi: [],
                    errors: {
                        exams: [],
                        levels: [],
                    },
                }),
            }),
            studentList: [],

            data: ref({
                session: null,
                branch: null,
                shift: null,
                class: null,
                section: null,
                group_name: null,
                semester: null,
                exam: null,
                examIds: [],
                record_ids: [],
                subject: null,
                tag: null,
                subject_object: null,
                exam_date: null,
                publish_time: null,
                exam_name: null,
                send_sms: null,
                hasGroups: false,
                existing_records: [],
                students: [],
                competency_based_curriculum: false,
                pis: [],
                bis: [],
                pi_bi_levels: [],
            }),
            multiExams: ref({}),
            fullMarks: ref({
                subjective: null,
                objective: null,
                practical: null,
                total: null,
            }),
            passMarks: ref({
                subjective: null,
                objective: null,
                practical: null,
                total: null,
            }),
            errors: ref({
                subjective: [],
                objective: [],
                practical: [],
                exam_date: null,
                publish_time: null,
                exam_name: null,
                exam_type: null,
                fullMarks: {
                    subjective: null,
                    objective: null,
                    practical: null,
                    total: null,
                },
                passMarks: {
                    subjective: null,
                    objective: null,
                    practical: null,
                    total: null,
                },
                remarks: [],
            }),
            errorsList: ref([]),
        }
    },
    computed: {
        piBiLevels() {
            return Object.keys(this.data.pi_bi_levels).map(key => ({ value: Number(key), label: this.data.pi_bi_levels[key] }))
        },
    },
    methods: {
        collect,
        moment(dateString, format) {
            moment.locale('en')
            return dateString ? moment(dateString, format ? format : 'DD-MM-YYYY') : moment()
        },
        initialErrorsState() {
            return {
                subjective: [],
                objective: [],
                practical: [],
                exam_date: null,
                publish_date: null,
                publish_time: null,
                exam_name: null,
                fullMarks: {
                    subjective: null,
                    objective: null,
                    practical: null,
                    total: null,
                },
                passMarks: {
                    subjective: null,
                    objective: null,
                    practical: null,
                    total: null,
                },
                remarks: [],
            }
        },
        initializeData(target, sourceExamRecord) {
          target.data.marks = []
          target.data.session = sourceExamRecord.session_id
          target.data.branch = sourceExamRecord.branch_id
          target.data.shift = sourceExamRecord.shift_id
          target.data.class = sourceExamRecord.class_id
          target.data.section = sourceExamRecord.section_id
          target.data.tag = sourceExamRecord.tag_id
          target.data.semester = sourceExamRecord.exam.semester_id
          target.data.subject = sourceExamRecord.subject_id
          target.data.subject_object = sourceExamRecord.subject
          target.data.exam_has_subj_obj_prac_breakdown = sourceExamRecord.exam.has_subj_obj_prac_breakdown
          target.data.max_count_per_semester_limit_reached = false
          target.data.exam_date = sourceExamRecord.exam_date
          target.data.publish_date = moment(sourceExamRecord.publishing_date, 'DD-MM-YYYY').toDate()
          target.data.publish_time = moment(sourceExamRecord.publishing_time, 'hh:mm A').toDate()
          target.data.exam_name = sourceExamRecord.name
          target.data.card_id = sourceExamRecord.card_id
          target.data.item_id = sourceExamRecord.item_id
          target.data.cards = sourceExamRecord.cards
          target.data.card_selection_enabled = sourceExamRecord.card_selection_enabled
          target.data.item_selection_enabled = sourceExamRecord.item_selection_enabled
          target.data.unsortedStudents = sourceExamRecord.marks.map(
              mark => {
                const data = {
                  display_id: mark.enrollment.student.display_id,
                  group_name: mark.enrollment.group_name,
                  enrollmentId: mark.enrollment_id,
                  roll: mark.enrollment.roll,
                  name: mark.enrollment.student.name,
                  photo: mark.enrollment.student.photo,
                  subjective: mark.subjective === '' ? null : Number(mark.subjective || 0),
                  objective: mark.objective === '' ? null : Number(mark.objective || 0),
                  practical: mark.practical === '' ? null : Number(mark.practical || 0),
                  total: Number(mark.subjective || 0) + Number(mark.objective || 0) + Number(mark.practical || 0),
                  is_absent: Boolean(mark.is_absent),
                  is_absent_subjective: Boolean(mark.is_absent_subjective),
                  is_absent_objective: Boolean(mark.is_absent_objective),
                  is_absent_practical: Boolean(mark.is_absent_practical),
                  is_not_eligible: mark.other_data?.is_not_eligible ? Boolean(mark.other_data?.is_not_eligible) : null,
                  remarks: mark.other_data?.remarks ?? null,
                  grade: mark.grade_letter,
                }

                if (this.competencyBasedCurriculum) {
                  data[sourceExamRecord.pi_id ? 'pi' : 'bi'] = {
                    0: mark.is_absent ? 1 : Number(mark.subjective)
                  }
                }
                return data
              }
          )
          target.data.students = target.data.unsortedStudents.sort(
              (a, b) => a.roll === b.roll ? (a.display_id < b.display_id ? -1 : 1) : (a.roll < b.roll ? -1 : 1)
          )
          target.data.has_groups = this.data.students.filter(student => student.group_name !== null).length > 0
          target.data.grades = sourceExamRecord.hasOwnProperty('grades') ? sourceExamRecord.grades : null

          target.fullMarks.subjective = sourceExamRecord.subjective_full_marks !== null ? Number(sourceExamRecord.subjective_full_marks) : null
          target.fullMarks.objective = sourceExamRecord.objective_full_marks !== null ? Number(sourceExamRecord.objective_full_marks) : null
          target.fullMarks.practical = sourceExamRecord.practical_full_marks !== null ? Number(sourceExamRecord.practical_full_marks) : null
          target.fullMarks.total = sourceExamRecord.full_marks !== null ? Number(sourceExamRecord.full_marks) : null
          target.passMarks.subjective = sourceExamRecord.subjective_pass_marks !== null ? Number(sourceExamRecord.subjective_pass_marks) : null
          target.passMarks.objective = sourceExamRecord.objective_pass_marks !== null ? Number(sourceExamRecord.objective_pass_marks) : null
          target.passMarks.practical = sourceExamRecord.practical_pass_marks !== null ? Number(sourceExamRecord.practical_pass_marks) : null
          target.passMarks.total = sourceExamRecord.pass_marks !== null ? Number(sourceExamRecord.pass_marks) : null
        },
        async loadStudents() {
            let params = new URLSearchParams()
            params.append('session', document.querySelector('#session').value)
            params.append('branch', document.querySelector('#branch').value)
            params.append('shift', document.querySelector('#shift').value)
            params.append('class', document.querySelector('#class').value)
            params.append('section', document.querySelector('#section').value)
            params.append('start_roll', document.querySelector('#start_roll')?.value ?? '')
            params.append('end_roll', document.querySelector('#end_roll')?.value ?? '')
            params.append('group_name', document.querySelector('#group_name').value)
            params.append('semester', document.querySelector('#semester').value)
            params.append('exam', document.querySelector('#exam').value)
            params.append('subject', document.querySelector('#subject').value)
            params.append('tag', document.querySelector('#tag')?.value ?? '')

            if (!this.validateSectionExamSubject(params)) {
                this.stopLoaders()

                return
            }

            let json;

            const response = await fetch(
                `${route('exams.records.list_students')}?${params}`,
                {
                    method: 'get',
                    headers: new Headers({
                        'X-Requested-With': 'XMLHttpRequest',
                    }),
                    credentials: 'include'  // adds cookie header
                }
            )
            if (response.status === 422) {
              const error = await response.json()
              toastr.error(`Failed fetching student list: ${ error.message }`)
              this.stopLoaders()
            } else if (!response.ok) {
                const error = await response.text()
                toastr.error(`Failed fetching student list: ${error}`)
                this.stopLoaders()
            } else {
                json = await response.json()

                if (json.data.competency_based_curriculum) {
                    for (let i = 0; i < json.data.students.length; i++) {
                        json.data.students[i].pi = []
                        json.data.students[i].bi = []

                        for (const i in 10) {
                            json.data.students[i].pi.push(-1)
                            json.data.students[i].bi.push(-1)
                        }
                    }
                }

                this.data = json.data
                this.studentList = json.data.students

                this.data.publish_date = moment(this.data.publish_date, 'DD-MM-YYYY').toDate()
                this.data.publish_time = moment(this.data.publish_time, 'hh:mm A').toDate()
                this.data.send_sms = this.sendSms;

                if (this.isMedicalCollege && this.data.exam_name?.includes('Term') && !this.data.exam_name?.includes('Supplementary')) {
                  this.data.students.map((student) => {

                    const failedCards = this.data.marks.filter(
                        (mark) => mark.enrollment_id === student.enrollmentId &&
                            mark.card_id && mark.item_id === null && mark.full_marks &&
                            mark.obtained_marks * 100 / mark.full_marks < 60,
                    ).map((mark) => mark.card_id)

                    const passedCards = this.data.marks.filter(
                        (mark) => mark.enrollment_id === student.enrollmentId &&
                            mark.card_id && mark.item_id === null && mark.full_marks &&
                            mark.obtained_marks * 100 / mark.full_marks >= 60,
                    ).map((mark) => mark.card_id)

                    let failedInCard = false
                    failedCards.forEach((cardId) => {
                      if (!passedCards.includes(cardId)) {
                        failedInCard = true
                      }
                    })
                    student.is_failed_card = failedInCard

                    return student
                  })
                }
            }

            const selectedExamId = document.querySelector('#exam').value;
            if (selectedExamId) {
              this.data.examIds = [selectedExamId];
            } else {
              this.data.examIds = [];
              const exams = Object.assign(
                  {},
                  ...(
                      [...document.querySelectorAll('#exam option')]
                          .filter(el => el.value !== '')
                          .map(el => {
                            this.data.examIds.push(el.value);
                            let obj = {}
                            obj[el.value] = el.text
                            return obj
                          })
                  )
              );

                for (let examId of Object.keys(exams)) {
                    this.multiExams[examId] = {
                        fullMarks: {
                            subjective: null,
                            objective: null,
                            practical: null,
                            total: null,
                        },
                        passMarks: {
                            subjective: null,
                            objective: null,
                            practical: null,
                            total: null,
                        },
                        data: {
                            students: JSON.parse(JSON.stringify(json.data.students)),
                        },
                        errors: this.initialErrorsState(),
                    }
                }

                this.data.exam = exams
            }
            
            if (!this.isEdit) {
                if (this.data.default_full_marks) {
                    this.fullMarks = {
                        subjective: this.data.subject_object.has_subjective ? this.data.default_full_marks : null,
                        objective: null,
                        practical: null,
                        total: null,
                    }
                } else {
                    this.fullMarks = {
                        subjective: null,
                        objective: null,
                        practical: null,
                        total: null,
                    }
                }
                this.calculateFullMarksTotal();
            }

            this.stopLoaders()

            this.$nextTick(() => {
                window.goToNextRowSameColumnInputOnEnter('vertical')
                window.disableMouseWheelOnNumberInputs()
                Ladda.bind('.ladda-button')
                $('.selectpicker').selectpicker('refresh')
            }).then(() => {})
        },
        isMultiExamMode() {
          return !this.data.competency_based_curriculum && this.data.examIds.length > 1;
        },
        orderedExamIds() {
          return this.data.examIds;
        },
        validateSectionExamSubject(formData) {
            let fieldLabels = {
                session: 'Session',
                branch: 'Branch',
                class: 'Class',
                semester: 'Semester',
                subject: 'Subject',
            }
            if (document.querySelector('#shift').required) {
                fieldLabels.shift = 'Shift'
            }
            if (document.querySelector('#section').required) {
                fieldLabels.section = 'Section'
            }
            if (this.isEdit || document.querySelector('#exam').required) {
                fieldLabels.exam = 'Exam'
            }

            for (let field of Object.keys(fieldLabels)) {
                if (!formData.get(field)) {
                    toastr.error(`Please select a ${fieldLabels[field]}.`);

                    return false
                }
            }

            return true;
        },
        stopLoaders() {
            setTimeout(() => window.Ladda.stopAll(), 500)
        },
        existingRecordLinks() {
            return this.data.existing_records.map(record =>
                `<a href="${route('exams.records.show', [record.id])}" target="_blank" class="alert-link">
                    ${record.name && record.name.length ? record.name : record.exam.name ? record.exam.name : '[unnamed]'}
                </a>`
            ).join(', ')
        },
        showTotalColumn() {
            const subject = this.data.subject_object

            return subject.has_subjective + subject.has_objective + subject.has_practical > 1
        },
        showGradeColumn() {
            return this.data.hasOwnProperty('grades') && this.data.grades?.length
        },
        hasErrors() {
            return this.errorsList.length > 0
        },
        onDateChanged(ref, date) {
            if (ref === 'exam_date') {
                this.data.exam_date = date
            } else if (ref === 'publish_date') {
                this.data.publish_date = date
            }
        },
        onTimeChanged(ref, time) {
          this.data.publish_time = time
        },
        onCardChange() {
            this.$nextTick(() => {
                $('.selectpicker').selectpicker('refresh')
            })
            if (this.data.marks.length > 0 && ['Card | Supplementary', 'Card | Re-Supplementary', 'Card | Special-Supplementary'].includes(this.data.exam_name)) {
              const failedCardStudentEnrollmentIds = this.data.marks.filter(
                  (mark) => mark.card_id === this.data.card_id && mark.item_id === null && mark.full_marks &&
                      mark.obtained_marks * 100 / mark.full_marks < 60,
                ).map((mark) => mark.enrollment_id)

              const passedCardStudentEnrollmentIds = this.data.marks.filter(
                  (mark) => mark.card_id === this.data.card_id && mark.item_id === null && mark.full_marks &&
                      mark.obtained_marks * 100 / mark.full_marks >= 60,
              ).map((mark) => mark.enrollment_id)

              this.data.students = this.studentList.filter((student) =>
                !passedCardStudentEnrollmentIds.includes(student.enrollmentId)
              );
            } else if (this.data.marks.length > 0 && this.data.exam_name?.includes('Card') && !this.data.exam_name?.includes('Supplementary')) {
              this.data.students = this.data.students.map((student) => {
                  const itemMarks = collect(this.data.marks).filter(
                      (mark) => mark.card_id === this.data.card_id && mark.enrollment_id === student.enrollmentId
                      && mark.full_marks
                  )
                  const itemCount = itemMarks.map((mark) => mark.item_id).unique().count()
                  const passedItemCount = itemMarks.filter(mark => mark.obtained_marks * 100.0 / mark.full_marks >= 60)
                      .map((mark) => mark.item_id).unique().count()

                  student.is_failed_item = passedItemCount < itemCount

                  return student
              })
            }
        },
        calculateFullMarksTotal(examId) {
            if (examId) {
                const marks = this.multiExams[examId].fullMarks
                this.multiExams[examId].fullMarks.total = (marks.subjective || 0) + (marks.objective || 0) + (marks.practical || 0)
            } else {
                const marks = this.fullMarks
                this.fullMarks.total = (marks.subjective || 0) + (marks.objective || 0) + (marks.practical || 0)
            }
        },
        calculatePassMarksTotal(examId) {
            if (examId) {
                const marks = this.multiExams[examId].passMarks
                this.multiExams[examId].passMarks.total = (marks.subjective || 0) + (marks.objective || 0) + (marks.practical || 0)
            } else {
                const marks = this.passMarks
                this.passMarks.total = (marks.subjective || 0) + (marks.objective || 0) + (marks.practical || 0)
            }
        },
        calculateObtainedTotal(studentIndex, examId) {
            let obtainedMarks = examId ? this.multiExams[examId].data.students[studentIndex] : this.data.students[studentIndex];
            let requiredPassMarks = examId ? this.multiExams[examId].passMarks : this.passMarks;
            obtainedMarks.total = (obtainedMarks.subjective ?? 0) + (obtainedMarks.objective ?? 0) + (obtainedMarks.practical ?? 0);

            let fullMarks = (examId ? this.multiExams[examId].fullMarks.total : this.fullMarks.total) ?? 0;

            if (this.showGradeColumn()) {
                this.calculateGrade(obtainedMarks, requiredPassMarks, fullMarks);
            }
        },
        isFailed(obtainedMarks, requiredPassMarks) {
          return obtainedMarks.subjective < requiredPassMarks.subjective ||
                 obtainedMarks.objective  < requiredPassMarks.objective ||
                 obtainedMarks.practical  < requiredPassMarks.practical ||
                 obtainedMarks.total      < requiredPassMarks.total;
        },
        calculateGrade(obtainedMarks, requiredPassMarks, fullMarks) {
            if (this.isFailed(obtainedMarks, requiredPassMarks)) {
                obtainedMarks.grade = 'F';
                return;
            }
            const percentage = Math.round(obtainedMarks.total / fullMarks * 100)
            const grade = this.data.grades.filter(
                grade => grade.from_marks_percentage <= percentage && grade.to_marks_percentage >= percentage
            )[0];

            if (grade) {
              obtainedMarks.grade = grade.grade_letter;
            }
        },
        onAbsentChanged(studentIndex, examId) {
            if (examId) {
                if (this.multiExams[examId].data.students[studentIndex].is_absent) {
                    this.multiExams[examId].data.students[studentIndex].subjective = null
                    this.multiExams[examId].data.students[studentIndex].objective = null
                    this.multiExams[examId].data.students[studentIndex].practical = null
                    this.multiExams[examId].data.students[studentIndex].total = null
                }
            } else {
                if (this.data.students[studentIndex].is_absent) {
                    this.data.students[studentIndex].subjective = null
                    this.data.students[studentIndex].objective = null
                    this.data.students[studentIndex].practical = null
                    this.data.students[studentIndex].total = null
                }
            }
        },
        onAbsentSubjectiveChanged(studentIndex, examId) {
          if (examId) {
            if (this.multiExams[examId].data.students[studentIndex].is_absent_subjective) {
              this.multiExams[examId].data.students[studentIndex].subjective = null
            }
          } else {
            if (this.data.students[studentIndex].is_absent_subjective) {
              this.data.students[studentIndex].subjective = null
            }
          }
          this.calculateObtainedTotal(studentIndex, examId);
        },
        onAbsentObjectiveChanged(studentIndex, examId) {
          if (examId) {
            if (this.multiExams[examId].data.students[studentIndex].is_absent_objective) {
              this.multiExams[examId].data.students[studentIndex].objective = null
            }
          } else {
            if (this.data.students[studentIndex].is_absent_objective) {
              this.data.students[studentIndex].objective = null
            }
          }
          this.calculateObtainedTotal(studentIndex, examId);
        },
        onAbsentPracticalChanged(studentIndex, examId) {
          if (examId) {
            if (this.multiExams[examId].data.students[studentIndex].is_absent_practical) {
              this.multiExams[examId].data.students[studentIndex].practical = null
            }
          } else {
            if (this.data.students[studentIndex].is_absent_practical) {
              this.data.students[studentIndex].practical = null
            }
          }
          this.calculateObtainedTotal(studentIndex, examId);
        },
        onRemoveEntry(studentIndex, examId) {
            if (examId) {
                this.multiExams[examId].data.students.splice(studentIndex, 1)
            } else {
                this.data.students.splice(studentIndex, 1)
            }
        },
        submitMarks: async function (save) {
            this.clearErrors()

            if (this.isMultiExamMode()) {
                await this.submitMultiExamMarks()

                this.stopLoaders()

                return
            }

            if (this.isEdit) {
                this.data.session = document.querySelector('#session').value
                this.data.branch = document.querySelector('#branch').value
                this.data.shift = document.querySelector('#shift').value
                this.data.class = document.querySelector('#class').value
                this.data.section = document.querySelector('#section').value
                this.data.tag = document.querySelector('#tag')?.value
                this.data.exam = document.querySelector('#exam').value
                this.data.subject = document.querySelector('#subject').value
            }

            let formData = {
                'session': this.data.session || null,
                'branch': this.data.branch || null,
                'shift': this.data.shift || null,
                'class': this.data.class || null,
                'section': this.data.section || null,
                'tag': this.data.tag || null,
                'exam': this.data.exam || null,
                'subject': this.data.subject || null,
                'exam_date': this.data.exam_date || null,
                'publishing_date': this.data.publish_date || null,
                'publishing_time': this.data.publish_time || null,
                'exam_name': this.data.exam_name || null,
                'bulk_entry_id': this.examRecord.bulk_entry_id || null,
                'send_sms': this.data.send_sms || null,
            };

            if (this.data.competency_based_curriculum) {
                if (this.isEdit) {
                    formData.exam_type = this.competencyBasedCurriculum.examType
                    formData.exam_id = this.competencyBasedCurriculum.exams[this.competencyBasedCurriculum.examType][0]
                    formData.record_ids = this.data.record_ids
                }

                await this.submitCompetencyBasedCurriculum(formData)

                this.stopLoaders()

                return
            }

            if (this.data.card_selection_enabled && !this.data.card_id) {
                this.errors.card_id = 'Card is required'
                this.errorsList.push('Card is required')

                toastr.error('Please correct the errors specified')

                this.stopLoaders()

                return
            }

            if (this.data.item_selection_enabled && !this.data.item_id) {
                this.errors.item_id = 'Item is required'
                this.errorsList.push('Item is required')

                toastr.error('Please correct the errors specified')

                this.stopLoaders()

                return
            }

            formData = {
                ...formData,
                'card_id': this.data.card_id,
                'item_id': this.data.item_id,
                'subjective_full_marks': this.fullMarks.subjective ?? null,
                'objective_full_marks': this.fullMarks.objective ?? null,
                'practical_full_marks': this.fullMarks.practical ?? null,
                'subjective_pass_marks': this.passMarks.subjective ?? null,
                'objective_pass_marks': this.passMarks.objective ?? null,
                'practical_pass_marks': this.passMarks.practical ?? null,
                'total_pass_marks': this.passMarks.total ?? null,
                'enrollmentIds': [],
                'subjective': [],
                'objective': [],
                'practical': [],
                'is_absent': [],
                'is_absent_subjective': [],
                'is_absent_objective': [],
                'is_absent_practical': [],
                'is_not_eligible': [],
                'remarks': [],
                'bulk_entry_id': this.examRecord.bulk_entry_id || null
            }

            if (this.data.competency_based_curriculum) {
                formData.enrollmentIds = {}
                for (let i = 0; i < this.data.students.length; i++) {
                    const student = this.data.students[i]
                    formData.enrollmentIds[student.enrollmentId] = student[this.competencyBasedCurriculum.examType]
                }
            } else {
              const eligibleStudents = this.data.students.filter((student) => !student.is_failed_item && !student.is_failed_card)
              eligibleStudents.map(student => student.enrollmentId)
                    .forEach(id => formData.enrollmentIds.push(id))
              eligibleStudents.map(student => student.subjective === 0 ? 0 : student.subjective || null)
                    .forEach(mark => formData.subjective.push(mark))
              eligibleStudents.map(student => student.objective === 0 ? 0 : student.objective || null)
                    .forEach(mark => formData.objective.push(mark))
              eligibleStudents.map(student => student.practical === 0 ? 0 : student.practical || null)
                    .forEach(mark => formData.practical.push(mark))
              eligibleStudents.map(student => student.is_absent)
                    .forEach(mark => formData.is_absent.push(mark))
              eligibleStudents.map(student => student.is_absent_subjective)
                  .forEach(mark => formData.is_absent_subjective.push(mark))
              eligibleStudents.map(student => student.is_absent_objective)
                    .forEach(mark => formData.is_absent_objective.push(mark))
              eligibleStudents.map(student => student.is_absent_practical)
                  .forEach(mark => formData.is_absent_practical.push(mark))
              eligibleStudents.map(student => student.is_not_eligible || null)
                    .forEach(mark => formData.is_not_eligible.push(mark))
              eligibleStudents.map(student => student.remarks || null)
                    .forEach(mark => formData.remarks.push(mark))
            }

            const response = await fetch(
                this.isEdit && !this.isDuplicating ? route('exams.records.update', {examRecord: this.examRecord.id ?? 0}) : route('exams.records.store'),
                {
                    method: 'post',
                    body: JSON.stringify(formData),
                    headers: new Headers({
                        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
                        'Content-Type': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                    }),
                    credentials: 'include'  // adds cookie header
                }
            )
            if (response.status === 422) {
                const json = await response.json()
                this.showSubmissionErrors(this.transformSubmissionErrors(json.errors))
            } else if (!response.ok) {
                const error = await response.text()
                toastr.error(`Failed creating exam records: ${error}`)
            } else {
                toastr.success('Exam marks recorded successfully')
                if (this.isEdit && !this.isDuplicating) {
                    if (!save) {
                        window.location = route('exams.records.show', [this.examRecord.id])
                    }
                } else if (save) {
                    const json = await response.json();
                    window.location = route('exams.records.edit', {examRecord: json.id ?? 0})
                } else {
                    window.location = `${route('exams.records.create')}?`
                        + `session=${this.data.session}&`
                        + `branch=${this.data.branch}&`
                        + `shift=${this.data.shift}&`
                        + `class=${this.data.class}&`
                        + `section=${this.data.section}&`
                        + `group=${this.data.group_name}&`
                        + `semester=${this.data.semester}&`
                        + `exam=${this.data.exam}&`
                        + `subject=${this.data.subject}&`
                        + `tag=${this.data.tag}&`
                }
            }

            this.stopLoaders()
        },
        async submitMultiExamMarks() {
            let formDataList = {};

            for (let examId of Object.keys(this.data.exam)) {
                const record = this.examRecords.find(rec => Number(rec.exam_id) === Number(examId));
                const formData = {
                    'id': record?.id || null,
                    'session': this.data.session || null,
                    'branch': this.data.branch || null,
                    'shift': this.data.shift || null,
                    'class': this.data.class || null,
                    'section': this.data.section || null,
                    'tag': this.data.tag || null,
                    'exam': examId,
                    'subject': this.data.subject || null,
                    'exam_date': this.data.exam_date || null,
                    'publishing_date': this.data.publish_date || null,
                    'publishing_time': this.data.publish_time || null,
                    'exam_name': this.data.exam_name || null,
                    'subjective_full_marks': this.multiExams[examId].fullMarks.subjective || null,
                    'objective_full_marks': this.multiExams[examId].fullMarks.objective || null,
                    'practical_full_marks': this.multiExams[examId].fullMarks.practical || null,
                    'subjective_pass_marks': this.multiExams[examId].passMarks.subjective || null,
                    'objective_pass_marks': this.multiExams[examId].passMarks.objective || null,
                    'practical_pass_marks': this.multiExams[examId].passMarks.practical || null,
                    'total_pass_marks': this.multiExams[examId].passMarks.total || null,
                    'enrollmentIds': [],
                    'subjective': [],
                    'objective': [],
                    'practical': [],
                    'is_absent': [],
                    'is_not_eligible': [],
                    'remarks': [],
                    'bulk_entry_id': this.examRecord.bulk_entry_id || null
                }
                this.multiExams[examId].data.students.map(student => student.enrollmentId)
                    .forEach(id => formData.enrollmentIds.push(id))
                this.multiExams[examId].data.students.map(student => student.subjective || null)
                    .forEach(mark => formData.subjective.push(mark))
                this.multiExams[examId].data.students.map(student => student.objective || null)
                    .forEach(mark => formData.objective.push(mark))
                this.multiExams[examId].data.students.map(student => student.practical || null)
                    .forEach(mark => formData.practical.push(mark))
                this.multiExams[examId].data.students.map(student => student.is_absent)
                    .forEach(mark => formData.is_absent.push(mark))
                this.multiExams[examId].data.students.map(student => student.is_not_eligible || null)
                    .forEach(mark => formData.is_not_eligible.push(mark))
                this.multiExams[examId].data.students.map(student => student.remarks || null)
                    .forEach(mark => formData.remarks.push(mark))

                if (!formData.subjective_full_marks
                    && !formData.objective_full_marks
                    && !formData.practical_full_marks
                    && !formData.subjective.filter(val => val > 0).length
                    && !formData.objective.filter(val => val > 0).length
                    && !formData.practical.filter(val => val > 0).length
                    && !formData.is_absent.filter(val => !!val).length
                    && !formData.is_not_eligible.filter(val => !!val).length
                    && !formData.remarks.filter(val => val !== null).length
                ) {
                    // Exam is ignored
                    continue
                }

                const response = await fetch(
                    route('exams.records.validate'),
                    {
                        method: 'post',
                        body: JSON.stringify(formData),
                        headers: new Headers({
                            'Content-Type': 'application/json',
                            'X-Requested-With': 'XMLHttpRequest',
                        }),
                        credentials: 'include'  // adds cookie header
                    }
                )

                if (response.status === 422) {
                    const json = await response.json()
                    this.showSubmissionErrors(this.transformSubmissionErrors(json.errors), examId)

                    return
                } else if (!response.ok) {
                    const error = await response.text()
                    toastr.error(`Failed validating exam records: ${error}`)

                    return
                }

                formDataList[examId] = formData
            }

            const response = await fetch(
                route('exams.records.create-bulk'),
                {
                    method: 'post',
                    body: JSON.stringify(formDataList),
                    headers: new Headers({
                        'Content-Type': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                    }),
                    credentials: 'include'  // adds cookie header
                }
            )
            if (!response.ok) {
                const error = await response.text()
                toastr.error(`Failed creating exam records: ${error}`)
            } else {
                toastr.success('Exam marks recorded successfully')
                window.location = `${route('exams.records.create')}?`
                    + `session=${this.data.session}&`
                    + `branch=${this.data.branch}&`
                    + `shift=${this.data.shift}&`
                    + `class=${this.data.class}&`
                    + `section=${this.data.section}&`
                    + `group=${this.data.group_name}&`
                    + `semester=${this.data.semester}&`
                    + `exam=&`
                    + `subject=${this.data.subject}&`
                    + `tag=${this.data.tag}&`
            }
        },
        async submitCompetencyBasedCurriculum(formData) {
            if (!this.validateCompetencyBasedCurriculumSubmission()) {
                this.stopLoaders()

                toastr.error('Please correct the errors specified')

                return
            }

            formData = {
                ...formData,
                'exam_type': this.competencyBasedCurriculum.examType,
                'exam_ids': this.competencyBasedCurriculum.exams[this.competencyBasedCurriculum.examType],
                'enrollmentIds': {},
                'isDuplicating': this.isDuplicating,
            }

            for (let i = 0; i < this.data.students.length; i++) {
                const student = this.data.students[i]
                formData.enrollmentIds[student.enrollmentId] = student[this.competencyBasedCurriculum.examType]
            }

            const response = await fetch(
                route('exams.records.create-competency-based-curriculum'),
                {
                    method: 'post',
                    body: JSON.stringify(formData),
                    headers: new Headers({
                        'Content-Type': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                    }),
                    credentials: 'include'  // adds cookie header
                }
            )
            if (!response.ok) {
                const error = await response.text()
                toastr.error(`Failed creating exam records: ${error}`)
            } else {
                toastr.success('Exam marks recorded successfully')
                window.location = `${route('exams.records.create')}?`
                    + `session=${this.data.session}&`
                    + `branch=${this.data.branch}&`
                    + `shift=${this.data.shift}&`
                    + `class=${this.data.class}&`
                    + `section=${this.data.section}&`
                    + `group=${this.data.group_name}&`
                    + `semester=${this.data.semester}&`
                    + `exam=${this.data.exam}&`
                    + `subject=${this.data.subject}&`
            }
        },
        validateCompetencyBasedCurriculumSubmission() {
            let hasErrors = false

            // At least one exam present
            let examsCount = this.competencyBasedCurriculum.exams[this.competencyBasedCurriculum.examType].length;
            if (examsCount <= 0) {
                this.competencyBasedCurriculum.exams.errors.exams[0] = 'Indicator is required'
                this.errorsList.push('Indicator is required')
                hasErrors = true
            }

            // Indicator is selected
            for (let colNo = 0; colNo < examsCount; colNo++) {
                const examId = this.competencyBasedCurriculum.exams[this.competencyBasedCurriculum.examType][colNo] || null
                if (!examId || examId === '') {
                    this.competencyBasedCurriculum.exams.errors.exams[colNo] = 'Indicator is required'
                    this.errorsList.push('Indicator is required')
                    hasErrors = true
                }
            }

            // No two indicators cannot be the same
            for (let i = 0; i < examsCount - 1; i++) {
                for (let j = i + 1; j < examsCount; j++) {
                    const examIdI = this.competencyBasedCurriculum.exams[this.competencyBasedCurriculum.examType][i];
                    const examIdJ = this.competencyBasedCurriculum.exams[this.competencyBasedCurriculum.examType][j];
                    if (examIdI === examIdJ) {
                        this.competencyBasedCurriculum.exams.errors.exams[j] = 'Duplicate indicator selected'
                        this.errorsList.push('Duplicate indicator selected')
                        hasErrors = true
                    }
                }
            }

            // Level of competence for all students have been selected
            // N.B. - we're skipping this for now. By default, if someone is not marked, they'll be marked as absent
            // for (let colNo = 0; colNo < examsCount; colNo++) {
            //     for (let studentIndex = 0; studentIndex < this.data.students.length; studentIndex++) {
            //         if (!((this.data.students[studentIndex][this.competencyBasedCurriculum.examType][colNo] ?? null) > 0)) {
            //             if (!this.competencyBasedCurriculum.exams.errors.levels?.[colNo]?.[studentIndex]) {
            //                 this.competencyBasedCurriculum.exams.errors.levels ??= {}
            //                 this.competencyBasedCurriculum.exams.errors.levels[colNo] ??= {}
            //             }
            //             this.competencyBasedCurriculum.exams.errors.levels[colNo][studentIndex] = 'Level of competence is required'
            //             this.errorsList.push('Level of competence is required')
            //             hasErrors = true
            //         }
            //     }
            // }

            return !hasErrors
        },
        transformSubmissionErrors(serverSideErrors) {
            const errors = this.initialErrorsState()
            let errorsList = []

            for (let prop in serverSideErrors) {
                const propParts = prop.split('.')
                if (propParts.length === 2) {
                    errors[propParts[0]][propParts[1]] = serverSideErrors[prop][0]
                } else {
                    switch (prop) {
                        case 'subjective_full_marks':
                            errors.fullMarks.subjective = serverSideErrors[prop][0]
                            break
                        case 'objective_full_marks':
                            errors.fullMarks.objective = serverSideErrors[prop][0]
                            break
                        case 'practical_full_marks':
                            errors.fullMarks.practical = serverSideErrors[prop][0]
                            break
                        case 'subjective_pass_marks':
                            errors.passMarks.subjective = serverSideErrors[prop][0]
                            break
                        case 'objective_pass_marks':
                            errors.passMarks.objective = serverSideErrors[prop][0]
                            break
                        case 'practical_pass_marks':
                            errors.passMarks.practical = serverSideErrors[prop][0]
                            break
                        case 'total_pass_marks':
                            errors.passMarks.total = serverSideErrors[prop][0]
                            break
                        default:
                            errors[prop] = serverSideErrors[prop][0]
                    }
                }

                errorsList.push(serverSideErrors[prop][0])
            }

            this.errorsList = errorsList

            return errors
        },
        showSubmissionErrors(errors, examId) {
            if (examId) {
                this.multiExams[examId].errors = errors
            } else {
                this.errors = errors
            }
            toastr.error(`Please correct the errors specified.`)
            window.scrollTo({top: 0, behavior: 'smooth'})
        },
        clearErrors() {
            this.errors = this.initialErrorsState()
            this.errorsList = []

            if (this.isMultiExamMode()) {
                for (let examId of Object.keys(this.data.exam)) {
                    this.multiExams[examId].errors = this.initialErrorsState()
                }
            }

            if (this.data.competency_based_curriculum) {
                this.competencyBasedCurriculum.exams.errors = {
                    exams: [],
                    levels: [],
                };
            }
        },
    },
    mounted() {
        if (this.isEdit) {
            this.competencyBasedCurriculum.examType = this.examRecord.pi_id ? 'pi' : 'bi'
            this.competencyBasedCurriculum.exams.pi = this.examRecord.pi_id && !this.examRecords?.length ? [this.examRecord.pi_id] : []
            this.competencyBasedCurriculum.exams.bi = this.examRecord.bi_id && !this.examRecords?.length ? [this.examRecord.bi_id] : []
            this.data.competency_based_curriculum = this.examRecord.pi_id || this.examRecord.bi_id
            this.data.pis = this.pis
            this.data.bis = this.bis
            this.data.pi_bi_levels = this.piBiLevelDetails
            if (this.data.competency_based_curriculum) {
                this.competencyBasedCurriculum.noOfColumns = this.examRecords.length
            }
            this.data.exam = this.examRecord.exam_id
            this.data.examIds = this.examRecords?.length > 0 ? [] : [this.examRecord.exam_id];
            this.data.record_ids = this.examRecords?.length > 0 ? [] : [this.examRecord.id];
            this.initializeData(this, this.examRecord, 0)
            if (this.examRecords
                && (this.examRecords.length > 1
                    || (this.examRecords.length === 1 && (this.examRecords[0].pi_id || this.examRecords[0].bi_id)))) {
                this.data.exam = {}
                let examIds = {};
                for (let index = 0; index < this.examRecords.length; index++) {
                    let examRecord = this.examRecords[index];
                    this.data.record_ids.push(examRecord.id);
                    this.data.exam[examRecord.exam_id] = examRecord.exam.name;
                    examIds[examRecord.exam.position] = examRecord.exam_id;
                    this.multiExams[examRecord.exam_id] = {
                        fullMarks: {
                            subjective: null,
                            objective: null,
                            practical: null,
                            total: null,
                        },
                        passMarks: {
                            subjective: null,
                            objective: null,
                            practical: null,
                            total: null,
                        },
                        data: {
                            students: this.data.students,
                        },
                        errors: this.initialErrorsState()
                    }
                    this.initializeData(this.multiExams[examRecord.exam_id], examRecord, index)
                    if (examRecord.pi_id) {
                        this.competencyBasedCurriculum.exams.pi.push(examRecord.pi_id)
                    }
                    if (examRecord.bi_id) {
                        this.competencyBasedCurriculum.exams.bi.push(examRecord.bi_id)
                    }
                    examRecord.marks.map(mark => {
                        this.data.students.map((student) => {
                            if (student.enrollmentId === mark.enrollment_id) {
                                if (!student[this.competencyBasedCurriculum.examType]) {
                                    student[this.competencyBasedCurriculum.examType] = {}
                                }
                                student[this.competencyBasedCurriculum.examType][index] = mark.is_absent ? 1 : Number(mark.subjective)
                            }
                        })
                    })
                }
                for (const display_id in Object.keys(examIds)) {
                  this.data.examIds.push(examIds[display_id]);
                }
            }
            for (let index = 0; index < this.data.students.length; index++) {
              if (this.isMultiExamMode()) {
                for (let examId of this.orderedExamIds()) {
                  this.calculateObtainedTotal(index, examId);
                }
              } else {
                this.calculateObtainedTotal(index);
              }
            }
            this.$nextTick(() => {
                window.goToNextRowSameColumnInputOnEnter('vertical')
                window.disableMouseWheelOnNumberInputs()
                Ladda.bind('.ladda-button')
                $('.selectpicker').selectpicker('refresh')
            }).then(() => {})
        }
    },
}
</script>

<style>
    #marks-entry-form th,
    #marks-entry-form td {
        vertical-align: middle;
    }

    #marks-entry-form tr:nth-child(1) td,
    #marks-entry-form tr:nth-child(2) td,
    #marks-entry-form tr:nth-child(3) td,
    #marks-entry-form td.highlight {
        background-color: #4E9EFF;
        border-top: 1px solid #3E8EF7 !important;
        border-bottom: 1px solid #3E8EF7 !important;
        color: #FFF !important;
        font-size: 16px;
        font-weight: 500!important;
    }
    #marks-entry-form tr:nth-child(1) td {
        text-align: center;
    }

    #marks-entry-form .abc-checkbox {
        padding-left: 12px;
    }
    #marks-entry-form .abc-checkbox.absent input[type="checkbox"] + label::after,
    #marks-entry-form .abc-checkbox.absent input[type="checkbox"]:checked + label::after {
        content: "✘" !important;
        padding-left: 9px;
    }

    /* Sticky header and column: https://css-tricks.com/making-tables-with-sticky-header-and-footers-got-a-bit-easier/ */
    #competency-based-assessment-form thead {
        position: sticky;
        top: 66px;
        z-index: 20;
    }

    #competency-based-assessment-form tbody th {
        position: sticky;
        z-index: 10;
        left: 260px;
    }

    #competency-based-assessment-form tbody th {
        background-color: #eef2f4;
        border-top-color: #e4eaec !important;
        border-bottom-color: #e4eaec !important;
    }

    @media (max-width: 576px) {
        #marks-entry-form th,
        #marks-entry-form td,
        #competency-based-assessment-form th,
        #competency-based-assessment-form td {
            vertical-align: top;
        }

        #competency-based-assessment-form tbody th {
            left: 0;
        }

        #competency-based-assessment-form thead tr:first-child th:nth-child(3) {
            position: sticky;
            left: 0;
            top: 66px;
            z-index: 20;
        }
    }

    @media (min-width: 768px) {
        .name {
            white-space: nowrap;
        }

        #marks-entry-form .abc-checkbox {
            margin-top: -20px;
        }

        #competency-based-assessment-form thead tr:first-child th:nth-child(3) {
            position: sticky;
            z-index: 20;
            left: 260px;
        }
    }
</style>
